import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';

const EXIT_ANIMATION_MS = 220;

interface ModalProps {
  open: boolean;
  onClose?: () => void;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  alert?: boolean;
  labelledBy?: string;
  describedBy?: string;
  className?: string;
  children: ReactNode;
}

export const Modal = ({
  open,
  onClose,
  closeOnBackdrop = true,
  closeOnEscape = true,
  alert = false,
  labelledBy,
  describedBy,
  className,
  children,
}: ModalProps) => {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const bodyRef = useRef<ReactNode>(children);
  if (open) bodyRef.current = children;

  useEffect(() => {
    if (open) {
      setClosing(false);
      setMounted(true);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, EXIT_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  useEffect(() => {
    if (!open || !closeOnEscape || !onClose) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, closeOnEscape, onClose]);

  if (!mounted) return null;

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdrop && event.target === event.currentTarget) onClose?.();
  };

  return (
    <div
      className={`bed-modal-backdrop${closing ? ' is-closing' : ''}`}
      role="presentation"
      onMouseDown={handleMouseDown}
    >
      <section
        className={`bed-modal${closing ? ' is-closing' : ''}${className ? ` ${className}` : ''}`}
        role={alert ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
      >
        {bodyRef.current}
      </section>
    </div>
  );
};
